/**
 * The per-session participant set, projected from the log (RFC 0233, renamed by
 * RFC 0292).
 *
 * A participant entry is what a session's ACCESS is made of: in Mod the session
 * log is the unit of access, so "who may sync this session" is derived here
 * rather than from a branch ACL. RFC 0233 wrote these entries by @-mentioning a
 * person in the composer, which is why they were called mentions and why the
 * projection used to expose a whole mention graph (`projectMentions`,
 * `inboxForMember`) that only the legacy surfaces ever read.
 *
 * RFC 0292 reclaims the word for a reference in a body of prose, which grants
 * nothing. What survives here is the half that grants: the entry, and the set it
 * projects to. Typing `@` in a comment must never be able to widen who can read
 * a session, and the surest way to guarantee that is for the two to share no
 * code and no name.
 *
 * THE WIRE VALUE IS UNCHANGED. The extension type is still `mod.object_mention`,
 * because every participant entry already written carries that string. Renaming
 * it would project as "no participants", which the sync gate reads as "admit
 * nobody but the creator" — access silently revoked on every existing session,
 * with nothing on screen to say why.
 *
 * The on-doc payload is flat (`targetKind`/`targetId`); the codec leaves an
 * extension's inner payload raw, so values are coerced with `String()` exactly
 * as `projectObjects` does.
 */

import { topoOrder } from './projection-order'
import type { LogDoc } from './types'
import { PARTICIPANT_EXTENSION } from './types'

/** One participant entry: `memberId` was admitted to `targetId` by `by`. */
interface ParticipantEntry {
  memberId: string
  by: string
  targetKind: string
  targetId: string
}

const TARGET_KINDS: ReadonlySet<string> = new Set([
  'session',
  'message',
  'document',
  'task',
])

/** Every well-formed participant entry on the log, in causal order. */
function participantEntries(doc: LogDoc): ParticipantEntry[] {
  const out: ParticipantEntry[] = []
  for (const e of topoOrder(doc)) {
    const p = e.payload
    if (p.type !== 'extension') continue
    if (p.payload.extensionType !== PARTICIPANT_EXTENSION) continue
    const raw = p.payload.payload
    const memberId = raw.memberId != null ? String(raw.memberId) : undefined
    const by = raw.by != null ? String(raw.by) : undefined
    const targetKind =
      raw.targetKind != null ? String(raw.targetKind) : undefined
    const targetId = raw.targetId != null ? String(raw.targetId) : undefined
    if (!memberId || !by || !targetKind || !targetId) continue
    if (!TARGET_KINDS.has(targetKind)) continue
    out.push({ memberId, by, targetKind, targetId })
  }
  return out
}

/**
 * RFC 0233 — the per-session participant set, derived from the session's OWN
 * log. Anyone admitted to it, plus whoever admitted them, is a participant. This
 * is the membership a per-session sync gate reads to decide who may sync the
 * session — no branch ACL, no separate members[] field.
 *
 * (Message authorship is not yet carried on message entries, so participation is
 * derived from these entries today; seed the creator separately at the call
 * site.)
 */
export function sessionParticipants(doc: LogDoc): Set<string> {
  const out = new Set<string>()
  for (const p of participantEntries(doc)) {
    if (p.targetKind !== 'session') continue
    out.add(p.memberId)
    out.add(p.by)
  }
  return out
}
